(() => {
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '--:--';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  class AudioTestimonialPlayer extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-initialized')) return;

      this.setAttribute('data-initialized', '');
      this.audio = this.querySelector('audio');
      this.toggleButton = this.querySelector('[data-audio-toggle]');
      this.progress = this.querySelector('[data-audio-progress]');
      this.currentTimeLabel = this.querySelector('[data-audio-current-time]');
      this.durationLabel = this.querySelector('[data-audio-duration]');

      if (!this.audio || !this.toggleButton || !this.progress) return;

      this.audio.controls = false;
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };

      this.toggleButton.addEventListener('click', () => this.togglePlayback(), listenerOptions);
      this.progress.addEventListener('input', () => this.seek(), listenerOptions);
      this.audio.addEventListener('loadedmetadata', () => this.updateDuration(), listenerOptions);
      this.audio.addEventListener('durationchange', () => this.updateDuration(), listenerOptions);
      this.audio.addEventListener('timeupdate', () => this.updateProgress(), listenerOptions);
      this.audio.addEventListener('play', () => this.handlePlay(), listenerOptions);
      this.audio.addEventListener('pause', () => this.handlePause(), listenerOptions);
      this.audio.addEventListener('ended', () => this.handleEnded(), listenerOptions);

      this.updateDuration();
      this.updateProgress();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.removeAttribute('data-initialized');
    }

    async togglePlayback() {
      if (this.audio.paused) {
        document.querySelectorAll('audio-testimonial-player audio').forEach((audio) => {
          if (audio !== this.audio && !audio.paused) audio.pause();
        });

        try {
          await this.audio.play();
        } catch (_error) {
          this.handlePause();
        }
      } else {
        this.audio.pause();
      }
    }

    handlePlay() {
      this.dataset.state = 'playing';
      this.toggleButton.setAttribute('aria-pressed', 'true');
      this.toggleButton.setAttribute('aria-label', this.toggleButton.dataset.pauseLabel);
    }

    handlePause() {
      this.dataset.state = 'paused';
      this.toggleButton.setAttribute('aria-pressed', 'false');
      this.toggleButton.setAttribute('aria-label', this.toggleButton.dataset.playLabel);
    }

    handleEnded() {
      this.audio.currentTime = 0;
      this.handlePause();
      this.updateProgress();
    }

    seek() {
      if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;

      this.audio.currentTime = (Number(this.progress.value) / 100) * this.audio.duration;
      this.updateProgress();
    }

    updateDuration() {
      if (this.durationLabel) this.durationLabel.textContent = formatTime(this.audio.duration);
    }

    updateProgress() {
      const duration = this.audio.duration;
      const percentage = Number.isFinite(duration) && duration > 0 ? (this.audio.currentTime / duration) * 100 : 0;

      this.progress.value = percentage;
      this.progress.style.setProperty('--audio-progress', `${percentage}%`);

      if (this.currentTimeLabel) this.currentTimeLabel.textContent = formatTime(this.audio.currentTime);
    }
  }

  if (!window.customElements.get('audio-testimonial-player')) {
    window.customElements.define('audio-testimonial-player', AudioTestimonialPlayer);
  }

  class AudioTestimonialsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-initialized')) return;

      this.carousel = this.querySelector('scroll-carousel');
      if (!this.carousel) return;

      this.setAttribute('data-initialized', '');
      this.abortController = new AbortController();
      this.interval = Math.max(Number(this.dataset.autoplayInterval) || 5000, 3000);
      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.audioPlaying = false;
      this.pointerHovering = false;
      this.focusWithin = false;
      const listenerOptions = { signal: this.abortController.signal };

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
          this.pointerHovering = true;
          this.stopAutoplay();
        }
      }, listenerOptions);
      this.addEventListener('pointerleave', () => {
        this.pointerHovering = false;
        this.scheduleAutoplay();
      }, listenerOptions);
      this.addEventListener('pointerdown', () => this.pauseAfterInteraction(), listenerOptions);
      this.addEventListener('focusin', () => {
        this.focusWithin = true;
        this.stopAutoplay();
      }, listenerOptions);
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;

        this.focusWithin = false;
        this.scheduleAutoplay();
      }, listenerOptions);

      this.querySelectorAll('audio').forEach((audio) => {
        audio.addEventListener('play', () => {
          this.audioPlaying = true;
          this.stopAutoplay();
        }, listenerOptions);
        audio.addEventListener('pause', () => {
          this.audioPlaying = this.hasPlayingAudio();
          this.scheduleAutoplay();
        }, listenerOptions);
        audio.addEventListener('ended', () => {
          this.audioPlaying = this.hasPlayingAudio();
          this.scheduleAutoplay();
        }, listenerOptions);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.scheduleAutoplay();
        }
      }, listenerOptions);
      this.reducedMotion?.addEventListener?.('change', () => {
        if (this.reducedMotion.matches) {
          this.stopAutoplay();
        } else {
          this.scheduleAutoplay();
        }
      }, listenerOptions);

      this.scheduleAutoplay();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopAutoplay();
      window.clearTimeout(this.interactionTimer);
      this.removeAttribute('data-initialized');
    }

    hasPlayingAudio() {
      return Array.from(this.querySelectorAll('audio')).some((audio) => !audio.paused && !audio.ended);
    }

    canAutoplay() {
      return !document.hidden
        && !this.reducedMotion?.matches
        && !this.audioPlaying
        && !this.pointerHovering
        && !this.focusWithin
        && this.carousel.visibleItems?.length > 1;
    }

    scheduleAutoplay(delay = this.interval) {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.advance();
        this.scheduleAutoplay();
      }, delay);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }

    pauseAfterInteraction() {
      this.stopAutoplay();
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = window.setTimeout(() => this.scheduleAutoplay(), 8000);
    }

    advance() {
      const visibleItems = this.carousel.visibleItems || [];
      if (visibleItems.length < 2) return;

      const currentIndex = Math.max(this.carousel.selectedIndexAmongVisible, 0);
      const lastPageIndex = Math.max((this.carousel.totalPages || visibleItems.length) - 1, 0);
      const nextVisibleIndex = currentIndex >= lastPageIndex ? 0 : currentIndex + 1;
      const nextItem = visibleItems[nextVisibleIndex];
      const nextIndex = this.carousel.items.indexOf(nextItem);

      if (nextIndex >= 0) this.carousel.select(nextIndex);
    }
  }

  if (!window.customElements.get('audio-testimonials-carousel')) {
    window.customElements.define('audio-testimonials-carousel', AudioTestimonialsCarousel);
  }
})();
